/**
 * ToggleButton component.
 *
 * Floating action button that opens/closes the chat panel.
 * Shows the chat icon when closed, close icon when open.
 * Supports an unread message badge.
 */

import { CHAT_ICON_SVG, CLOSE_ICON_SVG } from "./icons";
import { resolveLauncherIconUrl } from "./launcherIconUrl";

export interface ToggleButtonOptions {
  position: "bottom-right" | "bottom-left";
  onClick: () => void;
  /**
   * Optional custom launcher image (branding `launcherIconUrl`). When it
   * resolves to a safe http(s) URL the closed-state launcher renders the
   * image edge-to-edge instead of `CHAT_ICON_SVG`; the open ("X") state is
   * unaffected. A rejected URL or a failed image load falls back to the
   * built-in glyph, so a bad value can never leave the host site with an
   * empty or broken launcher.
   */
  launcherIconUrl?: string;
  /**
   * The embed's API endpoint. Used only to resolve the `{apiEndpoint}`
   * token in an entity preset's `launcherIconUrl`, so a self-hosted icon
   * follows the environment the embed is talking to.
   */
  apiEndpoint?: string;
}

export class ToggleButton {
  private readonly element: HTMLButtonElement;
  private readonly iconEl: HTMLSpanElement;
  private readonly badgeEl: HTMLSpanElement;
  /** Custom launcher image, or null when unset/rejected/failed to load. */
  private customIconEl: HTMLImageElement | null = null;
  private isOpen = false;
  private unreadCount = 0;

  constructor(private readonly options: ToggleButtonOptions) {
    this.element = document.createElement("button");
    this.element.className = "hana-toggle";
    this.element.setAttribute("aria-label", "Open chat");
    this.element.setAttribute("type", "button");

    if (options.position === "bottom-left") {
      this.element.classList.add("hana-toggle--left");
    }

    this.iconEl = document.createElement("span");
    this.iconEl.className = "hana-toggle__icon";
    this.iconEl.innerHTML = CHAT_ICON_SVG;
    this.element.appendChild(this.iconEl);

    this.initCustomIcon(options.launcherIconUrl, options.apiEndpoint);

    this.badgeEl = document.createElement("span");
    this.badgeEl.className = "hana-toggle__badge hana-toggle__badge--hidden";
    this.badgeEl.setAttribute("aria-live", "polite");
    this.element.appendChild(this.badgeEl);

    this.element.addEventListener("click", () => {
      this.options.onClick();
    });
  }

  /**
   * Mount the optional custom launcher image.
   *
   * The URL is gated by `resolveLauncherIconUrl()` (site-relative paths
   * resolved against the page; http/https only) before it ever reaches
   * `img.src`. If the image fails to load on the host site — 404,
   * blocked by the page's CSP `img-src`, hotlink protection — the error
   * handler tears the image back out and the built-in glyph is already
   * underneath it, so the launcher degrades to today's appearance rather
   * than rendering an empty circle.
   */
  private initCustomIcon(
    rawUrl: string | undefined,
    apiEndpoint: string | undefined,
  ): void {
    const url = resolveLauncherIconUrl(rawUrl, { apiEndpoint });
    if (!url) return;

    const img = document.createElement("img");
    img.className = "hana-toggle__image";
    // Decorative: the button already carries an aria-label ("Open chat"),
    // so announcing the image would duplicate it for screen readers.
    img.alt = "";
    img.setAttribute("aria-hidden", "true");
    img.addEventListener("error", () => this.dropCustomIcon());
    img.src = url;

    this.customIconEl = img;
    this.element.appendChild(img);
    this.element.classList.add("hana-toggle--custom-icon");
    // Closed state shows the image; hide the built-in glyph beneath it.
    this.iconEl.classList.add("hana-toggle__icon--replaced");
  }

  /** Revert to the built-in glyph after a failed image load. */
  private dropCustomIcon(): void {
    if (this.customIconEl) {
      this.customIconEl.remove();
      this.customIconEl = null;
    }
    this.element.classList.remove("hana-toggle--custom-icon");
    this.iconEl.classList.remove("hana-toggle__icon--replaced");
  }

  getElement(): HTMLButtonElement {
    return this.element;
  }

  setOpen(open: boolean): void {
    this.isOpen = open;
    this.iconEl.innerHTML = open ? CLOSE_ICON_SVG : CHAT_ICON_SVG;
    this.element.setAttribute("aria-label", open ? "Close chat" : "Open chat");

    // The close ("X") glyph always wins over a custom launcher image so an
    // open panel keeps a recognisable dismiss control.
    if (this.customIconEl) {
      this.customIconEl.classList.toggle("hana-toggle__image--hidden", open);
      this.iconEl.classList.toggle("hana-toggle__icon--replaced", !open);
      this.element.classList.toggle("hana-toggle--custom-icon", !open);
    }

    if (open) {
      this.setUnreadCount(0);
    }
  }

  setUnreadCount(count: number): void {
    this.unreadCount = count;
    if (count > 0 && !this.isOpen) {
      this.badgeEl.textContent = count > 99 ? "99+" : String(count);
      this.badgeEl.classList.remove("hana-toggle__badge--hidden");
    } else {
      this.badgeEl.textContent = "";
      this.badgeEl.classList.add("hana-toggle__badge--hidden");
    }
  }

  getUnreadCount(): number {
    return this.unreadCount;
  }

  setHidden(hidden: boolean): void {
    if (hidden) {
      this.element.classList.add("hana-toggle--hidden");
    } else {
      this.element.classList.remove("hana-toggle--hidden");
    }
  }
}
